import { OwnersContext, OwnersContextType } from '@/contexts/OwnersContext'
import { useDropdownData } from '@/hooks/useDropdownData'
import { useLinkProperty } from '@/hooks/useLinkProperty'
import { OwnerWithPropertyCount } from '@/types'
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useContext, useState } from 'react'

export type OwnerLinkPropertyModalProps = {
  isOpen: boolean
  onClose: () => void
  owner: OwnerWithPropertyCount
}

type PropertyDropdownOption = {
  id: string
  code: string
  title: string
}

export const OwnerLinkPropertyModal = ({
  isOpen,
  onClose,
  owner,
}: OwnerLinkPropertyModalProps) => {
  const [selectedPropertyId, setSelectedPropertyId] = useState('')
  const { filters } = useContext(OwnersContext) as OwnersContextType
  const {
    data: availableProperties,
    isLoading: isPropertiesLoading,
    error: propertiesError,
  } = useDropdownData<PropertyDropdownOption>('properties')
  const { linkPropertyAsync, isLinking } = useLinkProperty(filters)

  const handleCancel = () => {
    setSelectedPropertyId('')
    onClose()
  }

  const handleLinkProperty = async () => {
    if (!selectedPropertyId || !owner) return

    await linkPropertyAsync({
      propertyId: selectedPropertyId,
      ownerId: owner.id,
    })
    handleCancel()
  }
  if (propertiesError) {
    return (
      <Alert status="error">
        <AlertIcon />
        Erro ao carregar propriedades disponíveis: {propertiesError.message}
      </Alert>
    )
  }

  return (
    <Modal isOpen={isOpen} onClose={handleCancel}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Vincular Propriedade</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <VStack spacing={4} align="stretch">
            <Text color="gray.600">
              Selecione uma propriedade para vincular ao proprietário{' '}
              {owner?.name}:
            </Text>

            <FormControl>
              <FormLabel>Propriedade</FormLabel>
              {isPropertiesLoading || !availableProperties ? (
                <Box>
                  <Text color="gray.500">Carregando propriedades...</Text>
                </Box>
              ) : (
                <Select
                  value={selectedPropertyId}
                  onChange={(e) => setSelectedPropertyId(e.target.value)}
                  placeholder="Selecione uma propriedade..."
                  disabled={availableProperties.length === 0 || isLinking}
                >
                  {(availableProperties as PropertyDropdownOption[]).map(
                    (property) => (
                      <option key={property.id} value={property.id}>
                        {property.code} - {property.title}
                      </option>
                    )
                  )}
                </Select>
              )}
            </FormControl>
            <HStack justify="flex-end" pt={4} spacing={3}>
              <Button onClick={handleCancel} variant="ghost">
                Cancelar
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleLinkProperty}
                isDisabled={!selectedPropertyId || isLinking}
              >
                Vincular Propriedade
              </Button>
            </HStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
